import { HttpClient } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  OnDestroy,
  AfterViewInit,
} from '@angular/core';
import { FieldTypeConfig } from '@ngx-formly/core';
import { AnalyticsAwareFieldType } from '../analytics-aware-field-type';
import {
  Subscription,
  filter,
  finalize,
  distinctUntilChanged,
  merge,
} from 'rxjs';
import { GenericInputStatusEnum } from '../../../models/irembo-generic-input-status.enum';
import { IEnvironment } from '../../../models/environment.model';
import { v4 as uuidv4 } from 'uuid';
import {
  checkForValidFields,
  configureFields,
  populateFormFields,
  updateUrlWithApiGatewayBaseUrl,
  getPopulateReferenceForm,
  subscribeToResetFieldFetchData,
  handleErrorMapping,
  findFormControl,
} from '../../../../utils/utils/data-fetch-widget-utils';
import { FormStateService } from '../../../services/formly/form-state.service';
import { FormlyValueChangeEvent } from '@ngx-formly/core/lib/models';
import { AbstractControl } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { EIremboFormlyFormStateKeys } from '../../../models/irembo-formly-formstate-keys.enum';
@Component({
  selector: 'irembogov-custom-generic-button-fetch',
  templateUrl: './custom-generic-button-fetch.component.html',
  changeDetection: ChangeDetectionStrategy.Default,
})
export class CustomGenericButtonFetchComponent
  extends AnalyticsAwareFieldType<FieldTypeConfig>
  implements OnDestroy, AfterViewInit
{
  private subscriptions = new Subscription();
  statusClass: string | undefined;
  fieldsToPopulate: Record<string, string>[] = [];
  private environment: IEnvironment;
  isProcessing = false;
  private hasBeenClicked = false; // Track if button was clicked
  private payloadFieldsSubscription = new Subscription(); // Separate subscription for payload fields
  successMessage: string | undefined; // Track success message to display

  constructor(
    private http: HttpClient,
    private cd: ChangeDetectorRef,
    private translateService: TranslateService,
    private formStateService: FormStateService,
    @Inject('environment') environment: IEnvironment
  ) {
    super();
    this.environment = environment;
    this.defaultProps();
  }

  defaultProps() {
    if (this.field) {
      // Ensure props object exists
      this.field.props = this.field.props || {};

      // Set mandatory properties
      this.field.props = {
        ...this.field.props,
        hideLabel: true,
        buttonClicked: this.field.props['buttonClicked'] ?? false, // Initialize buttonClicked prop
      };
    }
  }

  ngAfterViewInit(): void {
    this.subscriptions.add(
      subscribeToResetFieldFetchData(
        this.field,
        this.formStateService,
        this.fieldsToPopulate,
        this.getFormReference()
      )
    );

    this.subscriptions.add(
      this.field.options?.fieldChanges
        ?.asObservable()
        .pipe(
          filter(
            (change: FormlyValueChangeEvent) =>
              change.field.key === this.field.key &&
              change.type === 'expressionChanges' &&
              change['property'] === 'props.runningAction'
          ),
          distinctUntilChanged(
            (a: FormlyValueChangeEvent, b: FormlyValueChangeEvent) =>
              a.value === b.value
          )
        )
        ?.subscribe((change: FormlyValueChangeEvent) => {
          if (change.value) {
            this.onButtonClick();
            return;
          }
          this.field.props['runAction'] = false;
        })
    );

    checkForValidFields(this.field);
    this.fieldsToPopulate = this.field.props?.['populates'] ?? [];

    if (this.fieldsToPopulate?.length > 0) {
      const formRef = this.getFormReference();

      this.subscriptions.add(
        configureFields(this.field, this.fieldsToPopulate, formRef)
      );
    }

    // Setup initial validation
    this.setupValidation();

    // Setup payload field listeners if needed
    this.setupPayloadFieldListeners();
  }

  private getFormReference() {
    if (this.field.props?.['populateRelativeToRootForm']) {
      let rootForm = this.form;
      while (rootForm.parent) {
        rootForm = rootForm.parent;
      }
      return rootForm;
    }
    return getPopulateReferenceForm(this.field, this.form);
  }

  private setupValidation(): void {
    // Initialize hasBeenClicked based on existing buttonClicked prop
    this.hasBeenClicked = this.field.props?.['buttonClicked'] ?? false;

    // Initial validation check
    this.validateComponent();

    // Set up custom validator
    if (this.formControl) {
      this.formControl.addValidators(this.customValidator.bind(this));
      this.formControl.updateValueAndValidity();
    }
  }

  private customValidator(
    control: AbstractControl
  ): { [key: string]: any } | null {
    // Check if field is required
    const isRequired = this.field?.props?.['isClickRequired'];

    if (!isRequired) {
      return null; // Not required, so always valid
    }

    // Check if button hasn't been clicked and it's required
    if (!this.hasBeenClicked) {
      return {
        buttonNotClicked: {
          message: this.translateService.instant('This action is required'),
        },
      };
    }

    return null;
  }

  private validateComponent(): void {
    if (this.formControl) {
      this.formControl.updateValueAndValidity();
      this.cd.detectChanges();
    }
  }

  private setupPayloadFieldListeners(): void {
    // Clear existing payload field subscriptions
    this.payloadFieldsSubscription.unsubscribe();
    this.payloadFieldsSubscription = new Subscription();

    // Only setup listeners if checkType is FIELD_LISTENER
    if (this.field.props?.['checkType'] !== 'FIELD_LISTENER') {
      return;
    }

    const formRef = getPopulateReferenceForm(this.field, this.form);
    const fieldsToWatch: string[] = [];
    const isInRFAState = this.formStateService.checkIfApplicationInAnRFAState();

    // Collect fields to watch from payload configuration
    const { payloadKey, payloadKeyMappings, useMultiplePayloadKeys } =
      this.field.props;

    if (useMultiplePayloadKeys && payloadKeyMappings?.length) {
      // Watch fields from payload key mappings
      payloadKeyMappings.forEach((mapping: any) => {
        if (!mapping.isStatic && mapping.sourceField) {
          fieldsToWatch.push(mapping.sourceField);
        }
      });
    } else if (payloadKey) {
      // Watch single payload key field
      fieldsToWatch.push(payloadKey);
    }

    // Setup listeners for each field
    if (fieldsToWatch.length > 0) {
      const validObservables = [];

      for (const fieldKey of fieldsToWatch) {
        const control =
          formRef.get(fieldKey) || findFormControl(formRef, fieldKey);

        if (control?.valueChanges) {
          // Apply distinctUntilChanged in RFA state to prevent duplicate emissions
          const observable = isInRFAState
            ? control.valueChanges.pipe(distinctUntilChanged())
            : control.valueChanges;

          validObservables.push(observable);
        }
      }

      if (validObservables.length > 0) {
        this.payloadFieldsSubscription.add(
          merge(...validObservables).subscribe(() => {
            this.onPayloadFieldChange();
          })
        );
      }
    }
  }

  private onPayloadFieldChange(): void {
    // When payload fields change and checkType is FIELD_LISTENER,
    // invalidate the component if it's required
    const isRequired = this.field?.props?.['isClickRequired'];

    if (isRequired && this.field.props?.['checkType'] === 'FIELD_LISTENER') {
      // Reset the clicked state to make it invalid again
      this.hasBeenClicked = false;

      // Update the buttonClicked prop
      this.field.props['buttonClicked'] = false;

      // Reset status class and success message
      if (this.statusClass === GenericInputStatusEnum.SUCCESS) {
        this.statusClass = undefined;
        this.successMessage = undefined;
      }

      // Trigger validation
      this.validateComponent();
    }
  }

  buildPayload(): Record<string, unknown> {
    const {
      useMultiplePayloadKeys,
      payloadKey,
      payloadKeyMappings,
      staticPayload,
    } = this.field.props;
    const formRef = this.getFormReference();

    if (staticPayload && typeof staticPayload === 'object') {
      return staticPayload;
    }

    if (!useMultiplePayloadKeys || !payloadKeyMappings?.length) {
      if (payloadKey) {
        let value;

        if (formRef.get(payloadKey)) {
          value = formRef.get(payloadKey)?.value;
        } else {
          const control = findFormControl(formRef, payloadKey);
          if (control) {
            value = control.value;
          }
        }

        return { [payloadKey]: value };
      }
      return {};
    }

    const payload: Record<string, unknown> = {};

    payloadKeyMappings.forEach((mapping: any) => {
      if (mapping.isStatic) {
        payload[mapping.key] = mapping.staticValue;
      } else if (mapping.sourceField) {
        let value;

        if (formRef.get(mapping.sourceField)) {
          value = formRef.get(mapping.sourceField)?.value;
        } else {
          const control = findFormControl(formRef, mapping.sourceField);
          if (control) {
            value = control.value;
          }
        }

        payload[mapping.key] = value;
      }
    });
    return payload;
  }

  onButtonClick() {
    if (this.isProcessing) {
      return;
    }

    // Mark as clicked (for internal tracking)
    this.hasBeenClicked = true;

    // Note: We don't set buttonClicked = true here yet
    // It will only be set to true on successful fetch

    // Reset status and success message if user clicks again after success
    if (this.statusClass === GenericInputStatusEnum.SUCCESS) {
      this.statusClass = undefined;
      this.successMessage = undefined;
      this.cd.detectChanges();
    }

    let { url } = this.field.props;
    const { endpointCode } = this.field.props;

    if (!url || !endpointCode) {
      throw new Error('url and endpointCode properties are required');
    }

    if (this.field.props['useBaseUrl']) {
      url = updateUrlWithApiGatewayBaseUrl(url, this.environment);
    }

    const payload = this.buildPayload();

    const params = {
      endpointCode,
      callerId: uuidv4(),
      payload,
      requester: uuidv4(),
    };
    this.isProcessing = true;
    this.statusClass = GenericInputStatusEnum.FETCHING;
    this.formStateService.clearGlobalError(this.field);

    this.subscriptions.add(
      this.http
        .post<Record<string, unknown>>(url, params)
        .pipe(
          finalize(() => {
            this.isProcessing = false;
            this.field.props['runAction'] = false;
          })
        )
        .subscribe({
          next: (res: Record<string, unknown>) => {
            this.statusClass = GenericInputStatusEnum.SUCCESS;
            // Emit before the unguarded res['data']['response'] parse below.
            this.emitFetchOutcome('success');
            const dataObj = res['data'] as Record<string, unknown>;
            const responseObj = dataObj['response'] as string;

            this.formStateService.saveFetchedDataKeyInFormState(
              this.field,
              responseObj,
              EIremboFormlyFormStateKeys.FETCHED_DATA,
              false
            );

            // FIXED: Clear validation state BEFORE population logic
            this.formControl.setErrors(null);
            this.field.props['buttonClicked'] = true;
            this.validateComponent();

            // Set success message if provided in props
            const successMessageProp =
              this.field.props?.['successMessageAfterFetch'];
            if (successMessageProp) {
              this.successMessage = successMessageProp;
            }

            // Now populate fields - validation is already clear
            if (this.fieldsToPopulate.length > 0) {
              const formReference = this.getFormReference();
              populateFormFields(responseObj, this.field, formReference);
            } else {
              this.field.options?.detectChanges?.(this.field);
            }

            // SUCCESS TICK AND MESSAGE WILL NOW PERSIST - No timeout to clear them
            // They will only disappear when the next fetch starts
          },
          error: error => {
            // Reset clicked state on error - user needs to click again after fixing the issue
            this.hasBeenClicked = false;

            // Ensure buttonClicked remains false on error
            this.field.props['buttonClicked'] = false;

            // Clear success message on error
            this.successMessage = undefined;

            const { errors, statusClass } = handleErrorMapping(
              error?.error || error,
              this.field.props['errorMapping'],
              'invalidAction',
              this.field,
              this.formControl,
              true
            );

            const errorKey = Object.keys(errors)[0];
            const errorMessage =
              errors[errorKey]?.message ||
              this.field.validation?.messages?.[errorKey];

            if (this.field?.props?.['showGlobalError'] && errorMessage) {
              this.formStateService.addGlobalError(this.field, errorMessage);
            }

            this.statusClass = statusClass;
            this.formControl.setErrors(errors);

            // Update validation to ensure required error is shown if applicable
            this.validateComponent();

            this.cd.detectChanges();
            this.emitFetchOutcome('failure', error);
          },
        })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.payloadFieldsSubscription.unsubscribe();
  }
}
